import React, { forwardRef, useMemo, memo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import ptBR from "date-fns/locale/pt-BR";
import { Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMaterialUIController } from "context";
import MDAccordionRoot from "components/MDAccordion/MDAccordionRoot";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { format, parse, parseISO, isValid } from "date-fns";
import { NumericFormat } from 'react-number-format';
import MDButton from "components/MDButton";

const noop = () => { };

const MDAccordionTable = forwardRef(({ color = "info", title, fields, items, handleInputChange = noop, handleButtonClick, ...rest }, ref) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const columns = useMemo(() => fields.map((field) => ({
    Header: (
      <MDTypography variant="caption" fontWeight="medium" color="text" textTransform="uppercase" fullWidth >
        {field.label}
      </MDTypography>
    ),
    accessor: field.name,
    width: `${field.width * 8.33}%`,
    Cell: ({ cell: { value }, row: { index } }) => {
      let displayValue = value ?? "";
      let inputType = field.type;

      if (field.type === "date" && value) {
        const parsedDate = value instanceof Date
          ? value
          : typeof value === "string" && value.includes("T")
            ? parseISO(value)
            : parse(value, "yyyy-MM-dd", new Date());

        if (isValid(parsedDate)) {
          displayValue = format(parsedDate, "yyyy-MM-dd", { locale: ptBR });
          inputType = "date";
        } else {
          console.error(`Data inválida no índice ${index}:`, value);
          displayValue = value;
        }
      }

      const commonProps = {
        fullWidth: true,
        variant: "outlined",
        size: "small",
        InputProps: { readOnly: !field.editable },
        sx: {
          backgroundColor: field.editable ? "inherit" : "#f0f0f0",
          color: "black",
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "rgba(0, 0, 0, 0.12)" },
          },
        }
      };

      switch (field.type) {
        case "button":
          return (
            <MDButton
              size="medium"
              variant="contained"
              color="warning"
              //sx={{ display: field.lastVisible ? (index === fields.length ? 'inline-flex' : 'none') : 'inline-flex' }}
              onClick={() => handleButtonClick(index, field.name, value)}
            >
              {field.name}
            </MDButton>
          );
        case "select":
          // Verifica se é o campo "UnitOfMeasurement"
          if (field.name === "UnitOfMeasurement") {
            const currentUomGroup = items[index]?.UomGroup || "";
            const options = currentUomGroup
              ? currentUomGroup.split(";").map(uom => ({
                Id: uom,
                Description: uom
              }))
              : [{ Id: displayValue, Description: displayValue }];

            return (
              <TextField
                {...commonProps}
                select
                value={displayValue ?? ""}
                onChange={(e) => handleInputChange(index, field.name, e.target.value)}
                disabled={!field.editable && options.length <= 1}
                InputProps={{
                  ...commonProps.InputProps,
                  readOnly: !field.editable && options.length <= 1
                }}
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    }
                  }
                }}
                sx={{
                  ...commonProps.sx,
                  width: "100%", // Garante largura total
                  minWidth: "150px", // Define uma largura mínima
                  backgroundColor: (!field.editable && options.length <= 1) ? "#f0f0f0" : "inherit", // Fundo cinza apenas se não for editável
                  "& .MuiOutlinedInput-root": {
                    height: "38px", // Define a altura do campo
                    display: "flex",
                    alignItems: "center",
                    padding: "0 14px", // Ajusta o espaçamento interno
                    color: !field.editable ? "black" : "inherit"
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "80%", // Garante alinhamento vertical
                  },
                  "& .MuiSelect-icon": {
                    display: "inline-block"
                  }
                }}
              >
                {options.length > 0 ? (
                  options.map((option) => (
                    <MenuItem key={option.Id || option} value={option.Id || option}>
                      {option.Description || option}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <em>Sem opções disponíveis</em>
                  </MenuItem>
                )}
              </TextField>
            );
          }
          else {
            // Caso genérico para outros selects normais
            return (
              <TextField
                {...commonProps}
                select
                value={displayValue}
                onChange={(e) => handleInputChange(index, field.name, e.target.value)}
              >
                {field.options.map((option) => (
                  <MenuItem key={option.Id || option} value={option.Id || option}>
                    {option.Description || option}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

        case "number":
        case "currency":
          return (
            <NumericFormat
              {...commonProps}
              customInput={TextField}
              value={displayValue}
              onValueChange={(values) => {
                handleInputChange(index, field.name, values.value);
              }}
              {...(field.type === "currency" ? {
                thousandSeparator: ".",
                decimalSeparator: ",",
                decimalScale: 5,
                fixedDecimalScale: true,
                allowNegative: false,
                allowLeadingZeros: true,
                prefix: "R$ "
              } : {
                decimalScale: 5,
                fixedDecimalScale: true,
                thousandSeparator: ".",
                decimalSeparator: ",",
                allowLeadingZeros: true
              })}
            />
          );
        case "date":
          return (

            <TextField

              style={{

                width: field.longField && displayValue ? `${displayValue.length * 10}px` : "100%",

              }}

              {...commonProps}

              value={displayValue}

              onChange={(e) => handleInputChange(index, field.name, e.target.value)}

              type={inputType}

            />

          );
        default:
          return (
            <TextField
              multiline
              maxRows={1}         // Altura máxima antes da rolagem
              style={{
                //width: field.longField && displayValue ? `${displayValue.length * 10}px` : "100%",
                width: field.longField && displayValue ? `${displayValue.length + 1}ch` : "100%",
                maxWidth: "500px",
                minWidth: "150px",
              }}
              inputProps={field?.inputProps}
              {...commonProps}
              value={displayValue}
              onChange={(e) => {
                handleInputChange(index, field.name, e.target.value)
              }}
              type={inputType}
            />
          );
      }
    },
  })), []);

  return (
    <MDAccordionRoot {...rest} ref={ref} ownerState={{ color, darkMode }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${title}-content`} id={`${title}-header`}>
          <MDTypography variant="h6" color={color === "white" ? "dark" : "white"}>
            {title}
          </MDTypography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <DataTable
            style={{ tableLayout: "auto", width: "100%" }}
            table={{ columns, rows: items }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </AccordionDetails>
      </Accordion>
    </MDAccordionRoot>
  );
});

MDAccordionTable.propTypes = {
  color: PropTypes.oneOf([
    "transparent", "white", "primary", "secondary", "info", "success", "warning", "error", "light", "dark",
  ]),
  title: PropTypes.node.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      name: PropTypes.string.isRequired,
      width: PropTypes.number,
      editable: PropTypes.bool,
      type: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleInputChange: PropTypes.func,
  handleButtonClick: PropTypes.func,
};

export default MDAccordionTable;